import React from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, Shield, Clock, Heart, 
  Star, Users, PawPrint, Camera,
  Menu, X, ArrowRight, Check,
  Image, MessageCircle, TreePine
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const APP_URL = "https://bowwow-dogpark.emergent.host/";

// Logo Component matching the app
const Logo = ({ size = "default" }) => {
  const sizeClasses = size === "large" ? "w-14 h-14" : "w-10 h-10";
  const iconSize = size === "large" ? "w-8 h-8" : "w-6 h-6";
  
  return (
    <div className={`${sizeClasses} rounded-full bg-gradient-to-br from-[#2A9DB0] to-[#E07800] flex items-center justify-center shadow-md`}>
      <div className={`${iconSize} text-white`}>
        <svg viewBox="0 0 24 24" fill="currentColor">
          <circle cx="8" cy="9" r="1.5" />
          <circle cx="16" cy="9" r="1.5" />
          <circle cx="12" cy="14" r="2" />
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fillOpacity="0.3"/>
        </svg>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDF8F3]/95 backdrop-blur-md border-b border-neutral-border">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="nav-logo">
            <Logo />
            <span className="font-fredoka text-2xl font-bold text-accent-charcoal">
              BowWowMeow
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-neutral-muted hover:text-primary transition-colors font-medium" data-testid="nav-features">
              Features
            </a>
            <a href="#how-it-works" className="text-neutral-muted hover:text-primary transition-colors font-medium" data-testid="nav-how-it-works">
              How It Works
            </a>
            <a href="#about" className="text-neutral-muted hover:text-primary transition-colors font-medium" data-testid="nav-about">
              About Us
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a href={APP_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-login-btn">
              <Button variant="ghost" className="text-accent-charcoal hover:text-primary font-semibold">
                Log In
              </Button>
            </a>
            <a href={APP_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-signup-btn">
              <Button className="bg-[#FF9500] hover:bg-[#E08600] text-white rounded-full px-6 font-semibold">
                Get Started
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X className="w-6 h-6 text-accent-charcoal" /> : <Menu className="w-6 h-6 text-accent-charcoal" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-neutral-border pt-4">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-neutral-muted hover:text-primary transition-colors font-medium">Features</a>
              <a href="#how-it-works" className="text-neutral-muted hover:text-primary transition-colors font-medium">How It Works</a>
              <a href="#about" className="text-neutral-muted hover:text-primary transition-colors font-medium">About Us</a>
              <div className="flex flex-col gap-2 mt-4">
                <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full rounded-full">Log In</Button>
                </a>
                <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-primary hover:bg-primary-hover text-white rounded-full">Get Started</Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="pt-32 pb-16 md:pb-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#F5A623]/20 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#3DB4C8]/10 rounded-full blur-3xl -z-10" />
      
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-background-accent rounded-full px-4 py-2 mb-6">
              <PawPrint className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-accent-charcoal">Trusted by 10,000+ Pet Parents</span>
            </div>
            
            <h1 className="font-fredoka text-4xl sm:text-5xl lg:text-6xl font-bold text-accent-charcoal leading-tight mb-6">
              We Are Like You...{' '}
              <span className="text-[#FF9500]">Dogs Are Better Than People</span>
              <span className="block text-[#2A9DB0]">, Cats Too!</span>
            </h1>
            
            <p className="text-lg text-neutral-muted mb-8 max-w-xl mx-auto lg:mx-0">
              Connect with trusted dog walkers and pet sitters in your neighborhood. 
              Real-time tracking, verified caregivers, and peace of mind for every pet parent.
            </p>

            {/* Dual CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Link to="/become-a-client" data-testid="hero-client-cta">
                <Button className="bg-[#FF9500] hover:bg-[#E08600] text-white rounded-full px-8 py-6 text-lg font-bold hover:scale-105 transition-transform shadow-lg w-full sm:w-auto">
                  Become a Client
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/become-a-partner" data-testid="hero-provider-cta">
                <Button variant="outline" className="bg-white text-accent-charcoal rounded-full px-8 py-6 text-lg font-bold hover:scale-105 transition-transform border-2 border-[#2A9DB0] hover:bg-[#2A9DB0] hover:text-white w-full sm:w-auto">
                  Become a Partner-Provider
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-[#3DB4C8]/20 border-2 border-white flex items-center justify-center">
                      <PawPrint className="w-4 h-4 text-[#3DB4C8]" />
                    </div>
                  ))}
                </div>
                <span className="text-sm text-neutral-muted">500+ Active Walkers</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#FF9500] fill-[#FF9500]" />
                <span className="text-sm text-neutral-muted">4.9 Average Rating</span>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1737534195272-4b71b9aec672?w=800&h=600&fit=crop"
                alt="Happy white dog running in green field"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="hero-image"
              />
              {/* Floating card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#3DB4C8] rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-fredoka font-bold text-accent-charcoal">Live GPS Tracking</p>
                    <p className="text-sm text-neutral-muted">Watch your pet's walk in real-time</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#3DB4C8]/30 rounded-full blur-xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#F5A623]/20 rounded-full blur-xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section with Bento Grid
const FeaturesSection = () => {
  const features = [
    {
      icon: MapPin,
      title: "Live GPS Tracking",
      description: "Follow your pet's walk in real-time on an interactive map. Know exactly where they are, every step of the way.",
      color: "text-[#2A9DB0]",
      bgColor: "bg-[#2A9DB0]/10",
      span: "md:col-span-2"
    },
    {
      icon: Shield,
      title: "Fully Insured",
      description: "All our services are fully insured for your peace of mind. Your pet is protected on every walk and visit.",
      color: "text-[#FF9500]",
      bgColor: "bg-[#FF9500]/10",
      span: ""
    },
    {
      icon: Check,
      title: "Verified Walkers",
      description: "Every walker is background-checked and verified. Your pet's safety is our priority.",
      color: "text-[#2A9DB0]",
      bgColor: "bg-[#2A9DB0]/10",
      span: ""
    },
    {
      icon: Camera,
      title: "Photo Updates",
      description: "Receive adorable photos during walks.",
      color: "text-[#FF9500]",
      bgColor: "bg-[#FF9500]/10",
      span: ""
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description: "Book walks on your schedule. Daily, weekly, or on-demand.",
      color: "text-[#2A9DB0]",
      bgColor: "bg-[#2A9DB0]/10",
      span: ""
    },
    {
      icon: Heart,
      title: "Pet Care Notes",
      description: "Share important details about your pet's needs, habits, and preferences with walkers.",
      color: "text-[#FF9500]",
      bgColor: "bg-[#FF9500]/10",
      span: "md:col-span-2"
    }
  ];

  return (
    <section id="features" className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-[#FF9500]/10 text-[#FF9500] font-semibold px-4 py-2 rounded-full text-sm mb-4">
            Features
          </span>
          <h2 className="font-fredoka text-3xl sm:text-4xl lg:text-5xl font-bold text-accent-charcoal mb-4">
            Everything Your Pet Needs
          </h2>
          <p className="text-lg text-neutral-muted max-w-2xl mx-auto">
            From GPS tracking to photo updates, we've got every pawsome feature covered.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-3 gap-6" data-testid="features-grid">
          {features.map((feature, index) => (
            <Card 
              key={index} 
              className={`feature-card ${feature.span} group cursor-pointer`}
              data-testid={`feature-card-${index}`}
            >
              <CardContent className="p-8">
                <div className={`w-14 h-14 ${feature.bgColor} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`w-7 h-7 ${feature.color}`} />
                </div>
                <h3 className="font-fredoka text-xl font-bold text-accent-charcoal mb-3">
                  {feature.title}
                </h3>
                <p className="text-neutral-muted">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Dog Park Icon Component
const DogParkIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.5 2 5.5 3.5 4 6c-1 1.5-1.5 3.5-1 5.5.3 1.2.8 2.3 1.5 3.2.5.7 1.2 1.3 2 1.8-.3.8-.5 1.7-.5 2.5 0 1.5.5 2.5 1.5 3 .8.4 1.8.4 2.5 0 .5-.3.8-.7 1-1.2.2.5.5.9 1 1.2.7.4 1.7.4 2.5 0 1-.5 1.5-1.5 1.5-3 0-.8-.2-1.7-.5-2.5.8-.5 1.5-1.1 2-1.8.7-.9 1.2-2 1.5-3.2.5-2-.1-4-1-5.5C18.5 3.5 15.5 2 12 2zm-3 7c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm6 0c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm-3 3c1.1 0 2 .7 2 1.5S13.1 15 12 15s-2-.7-2-1.5.9-1.5 2-1.5z"/>
    <circle cx="5" cy="5" r="1.5" opacity="0.5"/>
    <circle cx="19" cy="5" r="1.5" opacity="0.5"/>
    <path d="M3 12c-1 0-2 1-2 2s1 2 2 2" opacity="0.3"/>
    <path d="M21 12c1 0 2 1 2 2s-1 2-2 2" opacity="0.3"/>
  </svg>
);

// Community & Dog Park Section
const CommunitySection = () => {
  const [dogParkPosts, setDogParkPosts] = React.useState([]);
  const [loadingPosts, setLoadingPosts] = React.useState(true);

  React.useEffect(() => {
    const fetchDogParkPosts = async () => {
      try {
        const response = await fetch('https://walker-paysheet.preview.emergentagent.com/api/dog-park/public?limit=6&images_only=true');
        if (response.ok) {
          const posts = await response.json();
          setDogParkPosts(posts);
        }
      } catch (error) {
        console.error('Error fetching dog park posts:', error);
      } finally {
        setLoadingPosts(false);
      }
    };
    fetchDogParkPosts();
  }, []);

  const communityFeatures = [
    {
      icon: Image,
      title: "Pet Gallery",
      description: "Upload and share your pet's cutest photos and videos. Create a memory book of all their adventures and special moments.",
      color: "text-white",
      bgColor: "bg-[#2A9DB0]",
      isBlue: true
    },
    {
      icon: MessageCircle,
      title: "Community Hub",
      description: "Share stories, ask for advice, and connect with fellow pet lovers. Build lasting friendships in our supportive pet parent community.",
      color: "text-white",
      bgColor: "bg-[#FF9500]",
      isOrange: true
    },
    {
      icon: Users,
      title: "Meet Up",
      description: "Schedule playdates and meetups with other pet parents in your area. Let your furry friends make new friends too!",
      color: "text-white",
      bgColor: "bg-[#2A9DB0]",
      isBlue: true
    }
  ];

  return (
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#E07800]/5">
      <div className="max-w-7xl mx-auto">
        {/* Dog Park - Main Feature */}
        <div className="text-center mb-12">
          <span className="inline-block bg-[#2A9DB0] text-white font-semibold px-4 py-2 rounded-full text-sm mb-4">
            Your Gateway to Pet Services
          </span>
          <h2 className="font-fredoka text-3xl sm:text-4xl lg:text-5xl font-bold text-accent-charcoal mb-4">
            The Dog Park
          </h2>
          <p className="text-lg text-neutral-muted max-w-2xl mx-auto">
            Your central hub for all pet services. Explore, connect, and discover everything your furry friend needs.
          </p>
        </div>

        {/* Dog Park Hero Card */}
        <div className="mb-12">
          <Card className="rounded-3xl overflow-hidden bg-[#4CD964] text-white p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-fredoka text-2xl md:text-3xl font-bold mb-4">
                  Welcome to the Dog Park
                </h3>
                <p className="text-white/90 text-lg mb-6">
                  The Dog Park is your doorway to find dog-friendly parks, pet friendly events, connect with other pet parents, 
                  share memories, and build a community around your love for pets.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">Pet Gallery</span>
                  <span className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">Community Hub</span>
                  <span className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">Meet Ups</span>
                  <span className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">Local Parks</span>
                  <span className="bg-white/20 px-4 py-2 rounded-full text-sm font-medium">Shop</span>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="relative">
                  <PawPrint className="w-48 h-48 md:w-64 md:h-64 text-white/30" />
                  <Heart className="absolute top-0 right-0 w-12 h-12 text-white/50 fill-white/30" />
                  <Heart className="absolute top-16 -right-8 w-8 h-8 text-white/40 fill-white/20" />
                  <Heart className="absolute -top-4 right-16 w-6 h-6 text-white/50 fill-white/25" />
                  <Heart className="absolute bottom-8 -right-4 w-10 h-10 text-white/45 fill-white/20" />
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-3 gap-8" data-testid="community-grid">
          {communityFeatures.map((feature, index) => (
            <Card 
              key={index} 
              className={`rounded-3xl overflow-hidden group cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 ${feature.bgColor} text-white`}
              data-testid={`community-card-${index}`}
            >
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-fredoka text-xl font-bold mb-3 text-white">
                  {feature.title}
                </h3>
                <p className="text-white/90">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Community Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: "5K+", label: "Photos Shared" },
            { value: "2K+", label: "Community Members" },
            { value: "500+", label: "Dog Parks Listed" },
            { value: "10K+", label: "Stories & Tips" }
          ].map((stat, index) => (
            <div key={index} className="text-center p-6 bg-white rounded-2xl shadow-sm">
              <p className="font-fredoka text-3xl font-bold text-[#FF9500]">{stat.value}</p>
              <p className="text-sm text-neutral-muted mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Create Your Profile",
      description: "Add contact details, set billing and payment preferences.",
      forClient: true
    },
    {
      number: "02", 
      title: "Enter Your Pet's Details",
      description: "Select your preferred walking schedule and any special needs your pet might have.",
      forClient: true
    },
    {
      number: "03",
      title: "Get Started",
      description: "Real-time tracking of walks, text message with your walker mid-walk, and post-walk GPS tracking reports.",
      forClient: true
    }
  ];

  return (
    <section id="how-it-works" className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#2A9DB0]/15">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-[#2A9DB0] text-white font-semibold px-4 py-2 rounded-full text-sm mb-4">
            How It Works
          </span>
          <h2 className="font-fredoka text-3xl sm:text-4xl lg:text-5xl font-bold text-accent-charcoal mb-4">
            Getting Started is Easy
          </h2>
          <p className="text-lg text-neutral-muted max-w-2xl mx-auto">
            Three simple steps to happy walks for your furry friend.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-testid="how-it-works-steps">
          {steps.map((step, index) => (
            <div key={index} className="relative" data-testid={`step-${index}`}>
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-[60%] w-full h-0.5 bg-[#FF9500]/50" />
              )}
              
              <div className="bg-white rounded-3xl p-8 relative z-10 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-[#FF9500] rounded-2xl flex items-center justify-center mb-6">
                  <span className="font-fredoka text-2xl font-bold text-white">{step.number}</span>
                </div>
                <h3 className="font-fredoka text-xl font-bold text-accent-charcoal mb-3">
                  {step.title}
                </h3>
                <p className="text-neutral-muted">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Us Section
const AboutSection = () => {
  return (
    <section id="about" className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1576149324567-4fd5627ee73d?w=800&h=600&fit=crop"
                alt="Dog and cat friends together"
                className="w-full h-[400px] object-cover"
                data-testid="about-image"
              />
            </div>
            {/* Stats card */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-lg">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="font-fredoka text-3xl font-bold text-[#FF9500]">10K+</p>
                  <p className="text-sm text-neutral-muted">Happy Pets</p>
                </div>
                <div className="text-center">
                  <p className="font-fredoka text-3xl font-bold text-[#3DB4C8]">500+</p>
                  <p className="text-sm text-neutral-muted">Walkers</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="inline-block bg-[#FF9500]/10 text-[#FF9500] font-semibold px-4 py-2 rounded-full text-sm mb-4">
              About Us
            </span>
            <h2 className="font-fredoka text-3xl sm:text-4xl lg:text-5xl font-bold text-accent-charcoal mb-6">
              We're Pet Lovers, Just Like You
            </h2>
            <p className="text-lg text-neutral-muted mb-6">
              BowWowMeow was born from a simple idea: every pet deserves love, care, and adventure, 
              even when their humans are busy. We created a platform that connects passionate pet 
              caregivers with loving pet parents.
            </p>
            <p className="text-lg text-neutral-muted mb-8">
              Our community of verified walkers and sitters share one thing in common – they 
              absolutely adore animals. Whether it's a daily walk, overnight sitting, or just 
              some playtime, your pet is in good hands.
            </p>

            <div className="space-y-4">
              {[
                "Background-checked and verified caregivers",
                "Real-time GPS tracking on every walk",
                "Instant photo and video updates",
                "24/7 customer support"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[#3DB4C8] rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-accent-charcoal">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Walker CTA Section
const WalkerCTASection = () => {
  return (
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#FF9500]">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-white">
            <span className="inline-block bg-white/20 font-semibold px-4 py-2 rounded-full text-sm mb-4">
              Become a Partner-Provider
            </span>
            <h2 className="font-fredoka text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Turn Your Love for Pets Into Income
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Join our community of passionate pet caregivers. Set your own schedule, 
              work in your neighborhood, and get paid to spend time with adorable furry friends.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              {[
                { label: "Average per walk", value: "$25-50" },
                { label: "Weekly earnings", value: "$500+" },
                { label: "Flexible hours", value: "100%" },
                { label: "Support", value: "24/7" }
              ].map((stat, index) => (
                <div key={index} className="bg-white/20 rounded-2xl p-4">
                  <p className="font-fredoka text-2xl font-bold">{stat.value}</p>
                  <p className="text-white/80 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>

            <Link to="/become-a-partner" data-testid="walker-cta-btn">
              <Button className="bg-white text-[#FF9500] hover:bg-[#FFF5E6] rounded-full px-8 py-6 text-lg font-bold hover:scale-105 transition-transform">
                Become a Partner-Provider
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1763994684899-0df2a5f45896?w=800&h=600&fit=crop"
                alt="Man walking golden retriever"
                className="w-full h-[400px] object-cover"
                data-testid="walker-cta-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah M.",
      role: "Pet Parent",
      content: "BowWowMeow has been a lifesaver! I can finally see exactly where Max is during his walks. The photo updates make my day!",
      rating: 5
    },
    {
      name: "Mike T.",
      role: "Dog Walker",
      content: "I love being a BowWowMeow walker. The app makes everything so easy, and I get to meet the most adorable pups every day.",
      rating: 5
    },
    {
      name: "Jessica L.",
      role: "Pet Parent",
      content: "The peace of mind knowing Luna is with a verified, caring walker is priceless. Highly recommend to all pet parents!",
      rating: 5
    }
  ];

  return (
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#2A9DB0]/10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-[#2A9DB0] text-white font-semibold px-4 py-2 rounded-full text-sm mb-4">
            Testimonials
          </span>
          <h2 className="font-fredoka text-3xl sm:text-4xl lg:text-5xl font-bold text-accent-charcoal mb-4">
            Loved by Pet Parents & Walkers
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8" data-testid="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="bg-white rounded-3xl p-8" data-testid={`testimonial-${index}`}>
              <CardContent className="p-0">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#FF9500] fill-[#FF9500]" />
                  ))}
                </div>
                <p className="text-neutral-muted mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2A9DB0]/10 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#2A9DB0]" />
                  </div>
                  <div>
                    <p className="font-fredoka font-bold text-accent-charcoal">{testimonial.name}</p>
                    <p className="text-sm text-neutral-muted">{testimonial.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-4xl mx-auto text-center">
        <div className="bg-[#FF9500]/10 rounded-[2rem] p-8 md:p-16 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#FF9500]/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2A9DB0]/20 rounded-full blur-3xl" />
          
          <div className="relative z-10">
            <Logo size="large" />
            
            <h2 className="font-fredoka text-3xl sm:text-4xl lg:text-5xl font-bold text-accent-charcoal mb-4 mt-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-neutral-muted mb-8 max-w-2xl mx-auto">
              Join thousands of happy pet parents and passionate walkers. 
              Your pet's next adventure is just a tap away!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/become-a-client" data-testid="final-client-cta">
                <Button className="bg-[#FF9500] hover:bg-[#E08600] text-white rounded-full px-8 py-6 text-lg font-bold hover:scale-105 transition-transform shadow-lg w-full sm:w-auto">
                  Become a Client
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/become-a-partner" data-testid="final-provider-cta">
                <Button variant="outline" className="bg-white text-accent-charcoal rounded-full px-8 py-6 text-lg font-bold hover:scale-105 transition-transform border-2 border-[#2A9DB0] hover:bg-[#2A9DB0] hover:text-white w-full sm:w-auto">
                  Become a Partner-Provider
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#2C3E50] text-white py-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Logo />
              <span className="font-fredoka text-2xl font-bold">BowWowMeow</span>
            </div>
            <p className="text-white/70 max-w-md mb-6">
              Connecting pet parents with trusted dog walkers and pet sitters. 
              Because every pet deserves love, care, and adventure.
            </p>
            <div className="flex gap-4">
              <a href={APP_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#2A9DB0]/20 rounded-full flex items-center justify-center hover:bg-[#2A9DB0] transition-colors" data-testid="footer-app-link">
                <PawPrint className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-fredoka font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li><a href="#features" className="text-white/70 hover:text-white transition-colors">Features</a></li>
              <li><a href="#how-it-works" className="text-white/70 hover:text-white transition-colors">How It Works</a></li>
              <li><a href="#about" className="text-white/70 hover:text-white transition-colors">About Us</a></li>
              <li><a href={APP_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors">Download App</a></li>
            </ul>
          </div>

          {/* Get Started */}
          <div>
            <h4 className="font-fredoka font-bold text-lg mb-4">Get Started</h4>
            <ul className="space-y-3">
              <li><a href={APP_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors">Become a Client</a></li>
              <li><a href={APP_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors">Become a Partner-Provider</a></li>
              <li><a href={APP_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors">Sign Up</a></li>
              <li><a href={APP_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors">Log In</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © 2025 BowWowMeow. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-white/50 hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="text-white/50 hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#FDF8F3]" data-testid="landing-page">
      <Navigation />
      <HeroSection />
      <FeaturesSection />
      <CommunitySection />
      <HowItWorksSection />
      <AboutSection />
      <WalkerCTASection />
      <TestimonialsSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
};

export default LandingPage;
